import React, { memo, useCallback, useEffect, useState } from 'react'
import { Tabs, Collapse, Grid } from 'antd'
import Files from './tab-files'
import { Timeline } from './tab-timeline'
import { StagePlan } from '../stage-plan'
import { Chats } from './tab-chats/chats'
import ProductsTab from './tab-products-new'
import { getTimeLineData, getTimeLineFiles } from '../../../../../store/timeline/action-creators'
import { setTimelineClear } from '../../../../../store/timeline/actions'
import { setStagePlanClear } from 'src/react-app/store/stagePlan/actions'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'
import { StyledCollapse } from '../../../../../components/v2/field-group/styles'
import { Forms } from '../forms'
import EntityRelations from '../../../../../components/entity-relations'
import { TimelineTab } from '../../styles'
import { useAvitoChatDeepLink } from '../../../../../hooks/useAvitoChatDeepLink/useAvitoChatDeepLink'

const { useBreakpoint } = Grid

interface TabsProps {
  onChange?: (key: string) => void
  onTabClick?: (key: string) => void
  id: string | number
}

export const CardTabs: React.FC<TabsProps> = memo((props: TabsProps) => {
  const { onChange, onTabClick, id } = props
  const dispatch = useAppDispatch()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const { isChatsRequested, setRequestedTab } = useAvitoChatDeepLink()
  const [activeKey, setActiveKey] = useState<string>('data')
  const [timelineLoaded, setTimelineLoaded] = useState<boolean>(false)
  const [fullTimelineLoaded, setFullTimelineLoaded] = useState<boolean>(false)

  useEffect(() => {
    dispatch(setStagePlanClear())
    return () => {
      dispatch(setTimelineClear())
      dispatch(setStagePlanClear())
    }
  }, [dispatch])

  useEffect(() => {
    if (isChatsRequested && !isMobile) {
      setActiveKey('chats')
    }
  }, [isChatsRequested, isMobile])

  const handleOnChange = useCallback(
    key => {
      setActiveKey(key)
      setRequestedTab(key)
      if (id !== 'new') {
        if (key === 'timeline' && !fullTimelineLoaded) {
          dispatch(getTimeLineData('case', id))
          setTimelineLoaded(true)
          setFullTimelineLoaded(true)
        } else if (key === 'files' && !timelineLoaded) {
          dispatch(getTimeLineFiles('case', id))
          setTimelineLoaded(true)
        }
      }
      if (onChange) {
        onChange(key)
      }
    },
    [dispatch, id, timelineLoaded, fullTimelineLoaded, onChange, setRequestedTab],
  )

  const handleOnTabClick = useCallback(
    key => {
      if (onTabClick) {
        onTabClick(key)
      }
    },
    [onTabClick],
  )

  const tabsItems = [
    {
      key: 'data',
      label: 'Данные заказа',
      children: <Forms id={id} />,
    },
    ...(!isMobile
      ? [
          {
            key: 'products',
            label: 'Состав заказа',
            children: <ProductsTab id={id} />,
          },
        ]
      : []),
    {
      key: 'timeline',
      label: 'Лента событий',
      destroyOnHidden: true,
      children: isMobile ? (
        <>
          <StyledCollapse ghost>
            <Collapse.Panel header={'Прогноз исполнения'} key={'stage-plan-panel'}>
              <StagePlan id={id} hideHeader />
            </Collapse.Panel>
          </StyledCollapse>
          <StyledCollapse defaultActiveKey={['timeline-panel']} ghost>
            <Collapse.Panel header={'Лента событий'} key={'timeline-panel'}>
              <Timeline id={id} hideHeader />
            </Collapse.Panel>
          </StyledCollapse>
        </>
      ) : (
        <TimelineTab>
          <StagePlan id={id} />
          <Timeline id={id} />
        </TimelineTab>
      ),
    },
    ...(!isMobile
      ? [
          {
            key: 'chats',
            label: <span className="chatTabText">Чаты</span>,
            destroyOnHidden: true,
            children: <Chats id={id} />,
            className: 'chatBox',
          },
        ]
      : []),
    {
      key: 'files',
      label: 'Прикрепленные файлы',
      children: <Files id={id} />,
    },
    {
      key: 'relations',
      label: 'Связи',
      children: <EntityRelations entityType="order" entityId={id} />,
    },
  ]

  return (
    <Tabs
      onChange={handleOnChange}
      onTabClick={handleOnTabClick}
      activeKey={activeKey}
      items={tabsItems}
    />
  )
})

CardTabs.displayName = 'CardTabs'
